import { useEffect, useState } from 'react'
import { Sparkles, CheckCircle, Settings, Cog, Users, Database, Link, Moon, Sun } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectOption } from '@/components/ui/select'
import { Tabs } from '@/components/ui/tabs'
import { useSettingsStore, type GPTModel } from '@/stores/settings-store'
import { ImportExportSettings } from '@/components/settings/import-export-settings'
import { HouseholdSettings } from '@/components/household/household-settings'
import { aiRecipeParser } from '@/services/ai-recipe-parser'
import { useThemeStore } from '@/stores/theme-store'

export default function SettingsPage() {
  const { aiEnabled, gptModel, setAIEnabled, setGPTModel } = useSettingsStore()
  const { theme, toggleTheme } = useThemeStore()
  const [platformAIConfigured, setPlatformAIConfigured] = useState(false)

  useEffect(() => {
    void aiRecipeParser.getStatus(true).then(setPlatformAIConfigured)
  }, [])

  const toggleAI = () => {
    if (platformAIConfigured) {
      setAIEnabled(!aiEnabled)
    }
  }

  const tabs = [
    { id: 'ai', label: 'Slimme hulp', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'household', label: 'Huishoudens', icon: <Users className="h-4 w-4" /> },
    { id: 'integrations', label: 'Integraties', icon: <Link className="h-4 w-4" /> },
    { id: 'data', label: 'Data Beheer', icon: <Database className="h-4 w-4" /> },
  ]

  return (
    <div className="container max-w-4xl py-6 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="mb-2 flex items-center gap-3 text-2xl font-bold text-gray-900 sm:text-3xl">
          <Cog className="h-7 w-7 sm:h-8 sm:w-8" />
          App Instellingen
        </h1>
        <p className="text-gray-600">Beheer slimme hulp, huishoudens en je gegevens.</p>
      </div>

      <Tabs tabs={tabs} defaultTab="ai">
        <div>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                Weergave
              </CardTitle>
              <p className="text-sm text-gray-600">
                Kies of je de app in lichte of donkere kleuren wilt gebruiken.
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-600">
                Huidige stand: <span className="font-medium text-gray-900">{theme === 'dark' ? 'Donkere modus' : 'Lichte modus'}</span>
              </div>
              <Button variant="outline" onClick={toggleTheme}>
                {theme === 'dark' ? (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    Schakel naar licht
                  </>
                ) : (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    Schakel naar donker
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Slimme hulp
                {aiEnabled && platformAIConfigured ? (
                  <Badge variant="secondary" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Actief
                  </Badge>
                ) : null}
              </CardTitle>
              <p className="text-sm text-gray-600">
                Kies hier of de app hulp mag geven bij het overnemen van recepten en het maken van receptfoto&apos;s.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  <Settings className="mr-1 inline h-4 w-4" />
                  Snelheid en kwaliteit
                </label>
                <Select
                  value={gptModel}
                  onChange={(event) => setGPTModel(event.target.value as GPTModel)}
                  className="w-full"
                >
                  <SelectOption value="gpt-5.4">Meest nauwkeurig, iets langzamer (gpt-5.4)</SelectOption>
                  <SelectOption value="gpt-5.4-mini">Goede balans voor dagelijks gebruik (gpt-5.4-mini)</SelectOption>
                  <SelectOption value="gpt-5.4-nano">Snelste keuze voor simpele taken (gpt-5.4-nano)</SelectOption>
                  <SelectOption value="gpt-4.1">Oudere stabiele keuze (gpt-4.1)</SelectOption>
                </Select>
              </div>

              <div className="border-t pt-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="font-medium">Slimme hulp aanzetten</h4>
                    <p className="text-sm text-gray-600">
                      Hiermee kan de app recepttekst omzetten naar velden en optioneel een receptfoto maken.
                    </p>
                  </div>
                  <Button
                    variant={aiEnabled ? 'primary' : 'outline'}
                    size="sm"
                    onClick={toggleAI}
                    disabled={!platformAIConfigured}
                  >
                    {aiEnabled ? 'Uitschakelen' : 'Inschakelen'}
                  </Button>
                </div>
                {!platformAIConfigured ? (
                  <p className="mt-3 text-sm text-amber-700">
                    Slimme hulp werkt pas nadat de beheerder de serverkoppeling heeft ingesteld.
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <HouseholdSettings />
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Link className="h-5 w-5 mr-2" />
                Integraties
              </CardTitle>
              <p className="text-sm text-gray-600">
                Deze koppelingen gebruik je vanuit je weekmenu. Je hoeft ze hier niet in te stellen.
              </p>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-600">
              <div>
                <h4 className="font-medium text-gray-900">Albert Heijn</h4>
                <p>
                  Zet de boodschappen uit je weekmenu in één keer in je Albert Heijn-mandje. Gekozen producten worden
                  onthouden, zodat de match de volgende keer beter is.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Bring!</h4>
                <p>
                  Stuur je boodschappenlijst of een los recept door naar de Bring!-app. Hiervoor is geen account in deze
                  app nodig.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <ImportExportSettings />
        </div>
      </Tabs>
    </div>
  )
}
